import { createSelector } from 'reselect';

import { SYSTEM_DOMAIN_TYPES } from '../../constants/system_domains';
import type { AppState } from '../../store/types';

export function getCurrentSystemDomainId(state: AppState) {
  return (
    state.appReducer.differentSystemDomain?.id ??
    state.authReducer.userData.system_domain_id
  );
}

export const selectIsPlgTrial = createSelector(
  (state: AppState) => state.settings.merged.is_plg_domain,
  (state: AppState) => state.appReducer.systemDomainType,
  (isPlgDomain, systemDomainType) =>
    Boolean(isPlgDomain) && systemDomainType === SYSTEM_DOMAIN_TYPES.TRIAL,
);

/**
 * The current system domain's display name, preferring the lightweight
 * `system_domains_basic` entity over the full one. `undefined` until either
 * entity is in the store.
 */
export function getCurrentSystemDomainName(
  state: AppState,
): string | undefined {
  const domainId = getCurrentSystemDomainId(state);
  if (!domainId) {
    return undefined;
  }
  return (
    state.entities.system_domains_basic[domainId]?.name ??
    state.entities.system_domains[domainId]?.name ??
    undefined
  );
}

/**
 * Whether the current domain bought any storage gateway allowance. An unsold
 * limit is left out of `billing_limits` rather than reported as 0, and the full
 * system domain only arrives when a page loads it, so check that the fetch
 * succeeded before acting on a `false`.
 */
export function getHasIsgAllowance(state: AppState): boolean {
  const domainId = getCurrentSystemDomainId(state);
  if (!domainId) {
    return false;
  }
  const limits = state.entities.system_domains[domainId]?.billing_limits;
  return (
    (limits?.max_storage_gateway_clusters ?? 0) > 0 ||
    (limits?.max_storage_gateway_nodes ?? 0) > 0
  );
}

export function getCurrentPortalLimit(state: AppState): number | undefined {
  const domainId = getCurrentSystemDomainId(state);
  if (!domainId) {
    return undefined;
  }
  return (
    state.entities.system_domains_basic[domainId]?.billing_limits
      ?.max_portals ??
    state.entities.system_domains[domainId]?.billing_limits?.max_portals ??
    undefined
  );
}
